import { useScrollReveal } from "@/hooks/useScrollReveal";

export interface AIAnswerItem {
  question: string;
  answer: string;
  icon?: string;
}

interface AIAnswerBlockProps {
  title?: string;
  subtitle?: string;
  items: AIAnswerItem[];
}

function AnswerCard({ item, index }: { item: AIAnswerItem; index: number }) {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`group relative bg-white/5 border border-white/8 rounded-2xl p-6 transition-all duration-500 hover:border-gold-DEFAULT/35 hover:bg-white/8 cursor-default ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
      style={{ transitionDelay: `${index * 70}ms` }}
    >
      {/* Gold left accent bar */}
      <div className="absolute left-0 top-5 bottom-5 w-0.5 bg-gold-DEFAULT/40 rounded-full group-hover:bg-gold-DEFAULT/70 transition-colors duration-300" />

      <div className="pl-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 mt-0.5 bg-gold-DEFAULT/15 rounded-lg">
            <i className={`${item.icon || "ri-question-answer-line"} text-gold-DEFAULT text-sm`} />
          </div>
          <strong className="text-white text-sm font-semibold font-sans leading-snug">
            {item.question}
          </strong>
        </div>
        <p className="text-white text-sm leading-relaxed font-sans pl-10">
          {item.answer}
        </p>
      </div>
    </div>
  );
}

export default function AIAnswerBlock({
  title = "Quick Answers About United Auto Group",
  subtitle = "Everything you need to know about buying your next vehicle from Farmington's most trusted used car dealership.",
  items,
}: AIAnswerBlockProps) {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();

  const half = Math.ceil(items.length / 2);
  const left = items.slice(0, half);
  const right = items.slice(half);

  return (
    <section
      className="relative bg-charcoal py-20 md:py-28 overflow-hidden"
      aria-labelledby="ai-block-heading"
    >
      {/* Decorative blurred gold orb — top right */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gold-DEFAULT/6 rounded-full blur-3xl pointer-events-none -translate-y-1/2 translate-x-1/3" />
      {/* Decorative blurred gold orb — bottom left */}
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-gold-DEFAULT/5 rounded-full blur-3xl pointer-events-none translate-y-1/3 -translate-x-1/4" />

      {/* Top gold line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-DEFAULT/40 to-transparent" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-14 transition-all duration-700 ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="inline-flex items-center gap-2 mb-5 justify-center">
            <span className="h-px w-10 bg-gold-DEFAULT" />
            <span className="text-gold-DEFAULT text-xs font-bold tracking-[0.22em] uppercase font-sans">
              AI SEARCH READY
            </span>
            <span className="h-px w-10 bg-gold-DEFAULT" />
          </div>
          <h2
            id="ai-block-heading"
            className="font-serif text-white max-w-2xl mx-auto leading-tight mb-4"
            style={{ fontSize: "clamp(1.8rem, 3vw, 2.5rem)" }}
          >
            {title}
          </h2>
          <p className="text-white font-sans text-base max-w-xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        </div>

        {/* Two-column answer grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
          <div className="flex flex-col gap-3 md:gap-4">
            {left.map((item, i) => (
              <AnswerCard key={i} item={item} index={i} />
            ))}
          </div>
          <div className="flex flex-col gap-3 md:gap-4">
            {right.map((item, i) => (
              <AnswerCard key={i} item={item} index={i + half} />
            ))}
          </div>
        </div>

        {/* Bottom mid-page CTA */}
        <div className="mt-12 text-center">
          <p className="text-white/80 font-sans text-sm mb-5">
            Ready to move forward? Browse inventory or get pre-qualified in minutes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://www.uagmn.com/inventory/"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gold-DEFAULT text-charcoal font-bold text-sm px-9 py-4 rounded-full hover:bg-gold-light transition-colors duration-200 whitespace-nowrap cursor-pointer tracking-wide"
            >
              Browse Inventory
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </a>
            <a
              href="https://www.uagmn.com/get-pre-qualified/"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-9 py-4 rounded-full hover:border-white/45 hover:bg-white/5 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Get Pre-Qualified
            </a>
          </div>
        </div>
      </div>

      {/* Bottom gold line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-DEFAULT/30 to-transparent" />
    </section>
  );
}
